import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { formatTimeDisplay } from "@/lib/utils/time";
import { getCurrencySymbol } from "@/lib/utils/currency";

interface ImprovedFooterFourProps {
  date: Date;
  startTime: string;
  price: number;
  currency: string;
  language: string;
  participants: string;
  duration: string;
  isSoldOut?: boolean;
  onBookNow: () => void;
  className?: string;
  bookingType?: string;
  minChargeParticipants?: number | null;
  cutOffDays?: number;
}

const abbreviateLanguage = (language: string): string => {
  const abbreviations: Record<string, string> = {
    English: "EN",
    Swedish: "SE",
    German: "DE",
    Norwegian: "NO",
    Spanish: "ES",
    French: "FR",
  };
  return abbreviations[language] || language.slice(0, 2).toUpperCase();
};

const getMinimumPlayers = (groupSize: string): number => {
  const match = groupSize.match(/(\d+)-(\d+)/);
  if (match) {
    return parseInt(match[1]);
  }
  return 1;
};

export function ImprovedFooterFour({
  date,
  startTime,
  price,
  currency,
  language,
  participants,
  duration,
  isSoldOut = false,
  onBookNow,
  className = "",
  bookingType,
  minChargeParticipants,
  cutOffDays,
}: ImprovedFooterFourProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // Check if booking is within cut-off period
  const isWithinCutOff = (() => {
    if (!date || typeof cutOffDays !== 'number') return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const bookingDate = new Date(date);
    bookingDate.setHours(0, 0, 0, 0);

    const daysDifference = Math.floor((bookingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysDifference < cutOffDays;
  })();

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Summary section with better visual weight */}
      <div className="bg-muted/30 rounded-lg p-2">
        {/* Justified layout - spread across full width */}
        <div className="flex items-center justify-between text-sm sm:text-base">
          {/* Left aligned */}
          <div className="flex items-center gap-1.5">
            <span className="text-base">💰</span>
            <span className="font-semibold">
              {price} {getCurrencySymbol(currency)}
            </span>
          </div>

          {/* Center aligned */}
          <div className="flex items-center gap-1.5">
            <span className="text-base">📅</span>
            <span className="font-medium">
              {date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>

          {/* Right aligned */}
          <div className="flex items-center gap-1.5">
            <span className="text-base">⏰</span>
            <span className="font-medium">{formatTimeDisplay(startTime, locale)}</span>
          </div>
        </div>
      </div>

      {/* Cut-off warning when within cut-off period (only if not sold out) */}
      {isWithinCutOff && !isSoldOut && (
        <div className="py-1">
          <p className="text-xs text-amber-600 dark:text-amber-500 font-medium text-center">
            ⚠️ {t("booking.shows.card.cutOffNote", { count: cutOffDays ?? 7 })}
          </p>
        </div>
      )}

      {/* Book Now button */}
      <Button
        className="w-full h-10 text-sm sm:text-base"
        size="default"
        disabled={isSoldOut || isWithinCutOff}
        onClick={onBookNow}
      >
        {isSoldOut ? t("booking.shows.card.soldOut", "Sold Out") :
         t("booking.shows.card.bookNow", "Book Now")}
      </Button>

      {/* Subtle warnings */}
      <div className="text-[10px] text-muted-foreground text-center">
        <p>
          {minChargeParticipants && minChargeParticipants > 0 && (
            <>* {t("summary.bookingSummary.minimumCharge", "Minimum {{count}} participants charged", { count: minChargeParticipants })} • </>
          )}
          * {t("summary.prices", "Prices")} {bookingType === "company" ? t("summary.bookingSummary.excludingVat", "exclude VAT") : t("summary.bookingSummary.includingVat", "include VAT")}
        </p>
      </div>
    </div>
  );
}
